import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { SearchRefinementType } from '#/modules/search/constants/refinements/searchRefinementTypes';
import { SEARCH_REFINEMENT_NAMES } from '#/modules/search/constants/refinements/searchRefinementTypes';
import {
  useSearchFiltersByName,
  useSearchGetters,
} from '#/modules/search/contexts/SearchContext';
import { resetRefinementUI } from '#/modules/search/helpers/refinements/searchRefinementsResetRefinementUI';
import { searchGetPathSwitch } from '#/modules/search/routing/helpers/searchGetPathSwitch';
import type { SearchType } from '#/modules/search/types/SearchType';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

export function useSearchRefinements(searchType: SearchType) {
  const { locale } = useI18n();
  const { embedSearchWidget } = useEmbedSearchWidget();
  const [searchParams] = useSearchParams();
  const { getSearchQuery } = useSearchGetters();
  const navigate = useDeferredNavigate();
  const searchParamsRef = useLatest<URLSearchParams>(searchParams);
  const searchFiltersByName = useSearchFiltersByName();

  const hasActiveRefinements =
    SEARCH_REFINEMENT_NAMES[searchType].some((refinement) =>
      searchParams.has(refinement),
    ) ||
    (searchFiltersByName &&
      Object.values(searchFiltersByName).some((v) => {
        if (v === searchFiltersByName.type) return false;
        return Array.isArray(v) ? v.length > 0 : v;
      }));

  const clearAllRefinements = useCallback(() => {
    ([...searchParamsRef.current.keys()] as SearchRefinementType[]).forEach(
      (key) => {
        if (SEARCH_REFINEMENT_NAMES[searchType].includes(key)) {
          searchParamsRef.current.delete(key);
        }
      },
    );

    const newRoute = searchGetPathSwitch({
      locale,
      q: searchParamsRef.current.get('q') ?? getSearchQuery(),
      filtersByName: { type: searchFiltersByName?.type || 'JOB' },
      embedSearchWidgetId: embedSearchWidget?.id ?? null,
    });

    void navigate(newRoute, { replace: true });

    resetRefinementUI();

    trackClicked('Cleared All Search Filters');
  }, [
    locale,
    embedSearchWidget?.id,
    searchType,
    navigate,
    searchFiltersByName?.type,
    searchParamsRef,
    getSearchQuery,
  ]);

  return {
    clearAllRefinements,
    hasActiveRefinements,
  };
}
